<template>
  <v-dialog
    :value="dialogModel"
    max-width="1100"
    persistent
    eager
    scrollable
    content-class="cash-opening-dialog cash-opening-dialog--opening"
    overlay-opacity="0.46"
    @input="dialogModel = $event"
  >
    <v-card class="cash-opening-dialog__card">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Apertura de caja</span>
        <v-btn v-if="!hideCloseButton" icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text ref="dialogContent" class="cash-opening-dialog__content">
        <v-alert v-if="errorMessage" type="error" dense outlined>
          {{ errorMessage }}
        </v-alert>

        <v-alert v-if="successMessage" type="success" dense outlined>
          {{ successMessage }}
        </v-alert>

        <v-row class="mb-2 mt-0 cash-opening-dialog__status-row" no-gutters align="center">
          <v-col cols="12" md="8" class="pb-2 pb-md-0">
            <v-chip :color="hasActiveOpening ? 'success' : 'warning'" dark>
              {{ hasActiveOpening ? 'Caja abierta' : 'Caja pendiente de apertura' }}
            </v-chip>
          </v-col>
          <v-col cols="12" md="4" class="d-flex justify-md-end mt-2">
            <v-btn color="primary" small :loading="loading" @click="refreshData">
              <v-icon left small>mdi-reload</v-icon>
              Recargar
            </v-btn>
          </v-col>
        </v-row>

        <v-card flat class="pa-4">
          <v-row v-if="!hasActiveOpening">
            <v-col cols="12" md="7">
              <v-text-field
                v-model="openingForm.OpeningAmount"
                type="number"
                min="0"
                step="0.01"
                outlined
                dense
                label="Fondo inicial"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="5" class="d-flex align-end">
              <v-btn color="success" :loading="loading" @click="openCash">
                Aperturar caja
              </v-btn>
            </v-col>
          </v-row>

          <template v-else>
            <v-row>
              <v-col cols="12" md="4">
                <div><strong>Apertura:</strong> {{ formatDate(activeOpeningOpenedAt) }}</div>
              </v-col>
              <v-col cols="12" md="4">
                <div><strong>Fondo inicial:</strong> {{ formatAmount(activeOpeningAmount) }}</div>
              </v-col>
              <v-col cols="12" md="4">
                <div>
                  <strong>No. apertura:</strong> {{ activeOpeningDisplayId }}
                  <strong class="ml-3">Punto de venta:</strong> {{ currentPointSale || 'Sin definir' }}
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12" md="6">
                <v-select
                  v-model="selectedDateFilterType"
                  :items="dateFilterTypeOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  label="Usar fecha para el cierre"
                ></v-select>
              </v-col>
            </v-row>

            <v-row class="mt-2">
              <v-col cols="12" v-if="!isStrictCashClosing">
                <h3 class="mb-3">Totales de cierre calculados por la app</h3>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Detalle</th>
                      <th>Monto</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in displayedClosingMovements" :key="'movement-' + index">
                      <td>{{ item.detail }}</td>
                      <td>{{ formatAmount(item.amount) }}</td>
                      <td class="text-right">
                        <v-btn
                          v-if="item.canNavigateToCashControls"
                          text
                          small
                          color="primary"
                          @click="scrollToCashControls"
                        >
                          Ver registros
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="!displayedClosingMovements.length">
                      <td colspan="3" class="text-center">Sin datos</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <h3 class="mb-3">Arqueo de caja</h3>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Forma pago</th>
                      <th v-if="!isStrictCashClosing">Monto app</th>
                      <th>Monto usuario</th>
                      <th v-if="!isStrictCashClosing">Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in closeSummary.arqueoRows" :key="item.paymentMethod + '-' + index">
                      <td>{{ item.paymentMethod }}</td>
                      <td v-if="!isStrictCashClosing">{{ formatAmount(item.appAmount) }}</td>
                      <td style="min-width: 140px;">
                        <v-text-field
                          v-model="item.userAmount"
                          @input="updateDifference(item)"
                          type="number"
                          min="0"
                          step="0.01"
                          dense
                          hide-details
                          outlined
                        ></v-text-field>
                      </td>
                      <td v-if="!isStrictCashClosing">{{ formatAmount(item.differenceAmount) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12" md="6">
                <v-text-field
                  :value="formatAmount(calculatedClosingAmount)"
                  outlined
                  dense
                  readonly
                  label="Monto de cierre calculado"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-end justify-end">
                <v-btn
                  color="error"
                  :loading="loading"
                  :disabled="!canCloseCash"
                  @click="closeCash"
                >
                  Cerrar caja
                </v-btn>
              </v-col>
            </v-row>

            <v-row class="mt-6">
              <v-col cols="12">
                <div ref="cashControlsSection">
                  <h3 class="mb-3">Registros de caja chica</h3>
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Tipo</th>
                        <th>Detalle</th>
                        <th>Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in cashControls" :key="item.ID_CashControl || index">
                        <td>{{ formatDate(item.CreatedAt) }}</td>
                        <td>{{ getCashControlMovementTypeLabel(item.MovementType) }}</td>
                        <td>{{ item.Detail || "-" }}</td>
                        <td>{{ formatAmount(item.Amount) }}</td>
                      </tr>
                      <tr v-if="!cashControls.length">
                        <td colspan="4" class="text-center">Sin registros</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </v-col>
            </v-row>
          </template>
        </v-card>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "CashOpeningModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    status: {
      type: Object,
      default: () => ({}),
    },
    summaryTypeData: {
      type: String,
      default: "01",
    },
    hideCloseButton: {
      type: Boolean,
      default: false,
    },
    strictCashClosing: {
      type: Boolean,
      default: false,
    },
    targetOpeningId: {
      type: [Number, String],
      default: null,
    },
    openingOverride: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      openingForm: {
        OpeningAmount: null,
      },
      closeSummary: {
        closingMovements: [],
        arqueoRows: [],
      },
      cashControls: [],
      selectedDateFilterType: 1,
      dateFilterTypeOptions: [
        { text: "Fecha de factura", value: 1 },
        { text: "Fecha de registro / creacion", value: 2 },
      ],
      errorMessage: "",
      successMessage: "",
    };
  },
  computed: {
    dialogModel: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    activeOpeningData() {
      return this.openingOverride || this.status?.activeOpening || null;
    },
    hasActiveOpening() {
      return Boolean(this.activeOpeningData);
    },
    currentPointSale() {
      return this.activeOpeningData?.PointSale
        || this.activeOpeningData?.pointSale
        || this.status?.pointSale
        || null;
    },
    isStrictCashClosing() {
      if (this.strictCashClosing) {
        return true;
      }

      if (this.status?.strictCashClosing) {
        return true;
      }

      try {
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        return Boolean(storedUser.strictCashClosing || storedUser.StrictCashClosing);
      } catch (error) {
        return false;
      }
    },
    activeOpeningOpenedAt() {
      return this.activeOpeningData?.OpenedAt || this.activeOpeningData?.openedAt || null;
    },
    activeOpeningAmount() {
      return this.activeOpeningData?.OpeningAmount ?? this.activeOpeningData?.openingAmount ?? 0;
    },
    activeOpeningId() {
      return this.activeOpeningData?.ID_CashOpening || this.activeOpeningData?.openingId || null;
    },
    activeOpeningDisplayId() {
      return this.activeOpeningData?.DisplayOpeningId
        || this.activeOpeningData?.displayOpeningId
        || this.activeOpeningData?.correlativeId
        || this.activeOpeningData?.CorrelativeIdApertura
        || this.activeOpeningData?.Correlative_ID_apertura
        || this.activeOpeningId;
    },
    calculatedClosingAmount() {
      return this.closeSummary.arqueoRows.reduce((sum, item) => {
        const value = Number(item.userAmount);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0);
    },
    cashControlTotals() {
      return this.cashControls.reduce((totals, item) => {
        const amount = Number(item?.Amount || 0);
        if (!Number.isFinite(amount)) {
          return totals;
        }

        if (Number(item?.MovementType) === 2) {
          totals.income += amount;
        } else if (Number(item?.MovementType) === 1) {
          totals.expense += amount;
        }

        return totals;
      }, { income: 0, expense: 0 });
    },
    displayedClosingMovements() {
      const movements = Array.isArray(this.closeSummary.closingMovements)
        ? [...this.closeSummary.closingMovements]
        : [];

      const openingAmount = Number(this.activeOpeningAmount || 0);
      const cashIncomeAmount = Number(this.cashControlTotals.income || 0);
      const cashExpenseAmount = Number(this.cashControlTotals.expense || 0);

      if (Number.isFinite(openingAmount)) {
        movements.push({
          detail: "FONDO INICIAL",
          amount: openingAmount,
          canNavigateToCashControls: false,
        });
      }

      if (Number.isFinite(cashIncomeAmount)) {
        movements.push({
          detail: "INGRESOS DE CAJA CHICA",
          amount: cashIncomeAmount,
          canNavigateToCashControls: false,
        });
      }

      if (Number.isFinite(cashExpenseAmount)) {
        movements.push({
          detail: "EGRESOS DE CAJA CHICA",
          amount: -cashExpenseAmount,
          canNavigateToCashControls: true,
        });
      }

      return movements;
    },
    canCloseCash() {
      if (!this.hasActiveOpening) {
        return false;
      }
      return this.closeSummary.arqueoRows.length > 0
        && this.closeSummary.arqueoRows.every((item) => {
          const value = Number(item.userAmount);
          return Number.isFinite(value) && value >= 0;
        });
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.clearMessages();
          this.refreshData();
        }
      },
    },
    status: {
      deep: true,
      handler() {
        if (this.value) {
          this.refreshData();
        }
      },
    },
  },
  methods: {
    clearMessages() {
      this.errorMessage = "";
      this.successMessage = "";
    },
    async refreshData() {
      this.clearMessages();
      if (!this.hasActiveOpening) {
        this.closeSummary = {
          closingMovements: [],
          arqueoRows: [],
        };
        this.cashControls = [];
        return;
      }

      this.loading = true;
      try {
        const [summaryResponse, controlsResponse] = await Promise.all([
          this.$API.cashOpening.getCloseSummary({
            OpeningId: this.targetOpeningId || undefined,
            testTypeData: this.summaryTypeData || undefined,
            dateFilterType: this.selectedDateFilterType,
          }),
          this.$API.cashOpening.getControls({
            openingId: this.activeOpeningId || this.targetOpeningId || undefined,
          }),
        ]);
        const data = summaryResponse?.data || {};

        this.closeSummary = {
          closingMovements: Array.isArray(data.closingMovements) ? data.closingMovements : [],
          arqueoRows: Array.isArray(data.arqueoRows)
            ? data.arqueoRows
                .filter((item) => !item.isInformational)
                .map((item) => ({
                  ...item,
                  userAmount: 0,
                  differenceAmount: Number((0 - Number(item.appAmount || 0)).toFixed(2)),
                }))
            : [],
        };
        this.cashControls = Array.isArray(controlsResponse?.data) ? controlsResponse.data : [];
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || error?.message || "No se pudo cargar el resumen de cierre";
      } finally {
        this.loading = false;
      }
    },
    async openCash() {
      this.clearMessages();
      this.loading = true;
      try {
        const response = await this.$API.cashOpening.openCash(this.openingForm);
        this.successMessage = response?.message || "Caja aperturada correctamente";
        this.openingForm.OpeningAmount = null;
        this.$emit("status-updated", {
          action: "opened",
          opening: response?.data || null,
        });
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || error?.message || "No se pudo aperturar la caja";
      } finally {
        this.loading = false;
      }
    },
    async closeCash() {
      this.clearMessages();
      this.loading = true;
      try {
        const payload = {
          OpeningId: this.targetOpeningId || undefined,
          ClosingAmount: this.calculatedClosingAmount,
          testTypeData: this.summaryTypeData || undefined,
          dateFilterType: this.selectedDateFilterType,
          ArqueoRows: this.closeSummary.arqueoRows.map((item) => ({
            PaymentMethod: item.paymentMethod,
            Detail: item.detail || "",
            AppAmount: Number(item.appAmount || 0),
            UserAmount: Number(item.userAmount || 0),
          })),
        };

        const response = await this.$API.cashOpening.closeCash(payload);
        this.successMessage = response?.message || "Caja cerrada correctamente";
        this.$emit("status-updated", {
          action: "closed",
          opening: response?.data?.activeOpening || null,
          closingSummary: response?.data || null,
        });
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || error?.message || "No se pudo cerrar la caja";
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString();
    },
    formatAmount(value) {
      const amount = Number(value || 0);
      return amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getCashControlMovementTypeLabel(value) {
      return Number(value) === 1 ? "Egreso" : "Ingreso";
    },
    updateDifference(item) {
      const userAmount = Number(item.userAmount || 0);
      const appAmount = Number(item.appAmount || 0);
      item.differenceAmount = Number((userAmount - appAmount).toFixed(2));
    },
    scrollToCashControls() {
      const section = this.$refs.cashControlsSection;
      if (section && typeof section.scrollIntoView === "function") {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
  },
};
</script>

<style>
.cash-opening-dialog {
  z-index: 3000 !important;
}

.cash-opening-dialog--opening {
  max-height: calc(100vh - 32px) !important;
}

.cash-opening-dialog--opening .cash-opening-dialog__card {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 32px);
  overflow: hidden;
}

.cash-opening-dialog--opening .cash-opening-dialog__content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto !important;
}

.cash-opening-dialog__content {
  padding-top: 20px !important;
}
.cash-opening-dialog__status-row {
  margin-top: 0 !important;
}
</style>
